import { OrderBundleDto, OrderBundleUnitDto } from '../dtos/order-bundle.dto';
import {
  OrderCreationDto, OrderDetailDto, OrderNonAdminCreationDto,
  OrderUpdateDto,
} from '../dtos/order.dto';
import {
  Order,
  OrderCreation, OrderDetail, OrderNonAdminCreation,
  OrderUpdate,
  OrderWithCredentials,
} from '../models/order';
import { OrderBundle, OrderBundleUnit } from '../models/order-bundle';
import { dateMapper } from './date.mapper';
import { orderItemMapper } from './order-item.mapper';
import { packageMapper } from './package.mapper';
import { paymentItemMapper } from './payment-item.mapper';
import { productMapper } from './product.mapper';
import { refundMapper } from './refund.mapper';
import { userMapper } from './user.mapper';

/**
 * Map order from dto to model.
 * @param {object} dto Dto data.
 * @returns {Order}
 */
function fromDto(dto) {
  return new Order({
    id: dto.id,
    userId: dto.user_id,
    discountCode: dto.discount_code,
    discountAmount: dto.discount_amount,
    subTotal: dto.sub_total,
    tax: dto.tax,
    total: dto.total,
    balance: dto.balance,
    isPaid: dto.is_paid,
    isSelfOrder: dto.is_self_order,
    isPfsOrder: dto.is_pfs_order,
    isAllowAssign: dto.allow_assign,
    createdByUserId: dto.created_by,
    updatedByUserId: dto.updated_by,
    hasAssignments: dto.has_assignments,
    createdDate: dateMapper.fromDto(dto.create_date),
    editDate: dateMapper.fromDto(dto.edit_date),
    items: dto.items.map(item => orderItemMapper.fromDto(item)),
    user: userMapper.fromDto(dto.user),
    payment: dto.payment != null ? paymentItemMapper.fromDto(dto.payment) : null,
    createdByUser: dto.created_by_user ? userMapper.fromDto(dto.created_by_user) : null,
    updatedByUser: dto.updated_by_user ? userMapper.fromDto(dto.updated_by_user) : null,
    paymentRegistrations: dto.payment_registrations,
    studentNames: dto.student_names ?? [],
    classDates: dto.class_dates ?? [],
    studentDetails: dto.student_details ?? [],
  });
}

/**
 * From order with credential DTO.
 * @param {Order} orderDto Order DTO.
 * @param {object} accountDto Account DTO.
 * @returns {OrderWithCredentials}
 */
function fromOrderWithCredentialDto(orderDto, accountDto) {
  return new OrderWithCredentials({
    order: fromDto(orderDto),
    account: {
      username: accountDto.username,
      password: accountDto.password,
    },
  });
}

/**
 * Map order detail from dto.
 * @param {OrderDetailDto} dto Order detail dto.
 * @returns {OrderDetail}
 */
function detailFromDto(dto) {
  return new OrderDetail({
    id: dto.id,
    user: userMapper.fromDto(dto.user),
    refunds: dto.refunds.map(refund => refundMapper.fromDto(refund)),
  });
}

/**
 * Map order creation dto.
 * @param {OrderCreation} data Order creation data.
 * @returns {OrderCreationDto}
 */
function toCreationDto(data) {
  return new OrderCreationDto({
    user_id: data.userId,
    items: data.items.map(item => orderItemMapper.toCreationDto(item)),
  });
}

/**
 * Map order creation dto.
 * @param {OrderNonAdminCreation} data Order creation data.
 * @returns {OrderNonAdminCreationDto}
 */
function toNonAdminCreationDto(data) {
  return {
    user_id: data.userId,
    items: data.items.map(p => ({
      package_id: p.packageId,
      qty: p.quantity,
      discount_custom_amount: p.discountCustomAmount,
      state_code: p.stateCode ?? '',
    })),
    discounts: data.discounts.map(discount => ({
      code: discount,
    })),
  };
}

/**
 * Map order bundle unit from dto.
 * @param {OrderBundleUnitDto} dto Order bundle unit dto.
 * @returns {OrderBundleUnit}
 */
function orderBundleUnitFromDto(dto) {
  return new OrderBundleUnit({
    id: dto.id,
    isRefunded: dto.is_refunded,
    product: productMapper.fromDto(dto.product),
    assignee: dto.assignee ? userMapper.mapBaseUserFromDto(dto.assignee) : null,
  });
}

/**
 * Map order bundle from dto.
 * @param {OrderBundleDto} dto Order bundle dto.
 * @returns {OrderBundle}
 */
function orderBundleFromDto(dto) {
  return new OrderBundle({
    bundleId: dto.bundle_id,
    package: packageMapper.basePackageFromDto(dto.package),
    units: dto.units.map(unit => orderBundleUnitFromDto(unit)),
  });
}

/**
 * Map order assign to dto.
 * @param {OrderUpdate} model Order assign model.
 * @returns {OrderUpdateDto}
 */
function toOrderUpdateDto(model) {
  return new OrderUpdateDto({
    allow_assign: model.isAllowAssign ?? undefined,
    is_paid: model.isPaid ?? undefined,
  });
}

export const orderMapper = {
  fromDto,
  toCreationDto,
  toNonAdminCreationDto,
  orderBundleFromDto,
  detailFromDto,
  toOrderUpdateDto,
  fromOrderWithCredentialDto,
};
